import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Input,
  DatePicker,
  Modal,
  Descriptions,
  Badge,
  Divider,
  Tooltip,
  Image,
  Switch
} from 'antd';
import {
  InboxOutlined,        // thay thế PackageOutlined
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,     // thay thế WeightOutlined
  FilterOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  BugOutlined,
  TruckOutlined
} from '@ant-design/icons';
import { packingService, invoiceService } from '../../services';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const PackingList = () => {
  const [loading, setLoading] = useState(false);
  const [packingOrders, setPackingOrders] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} đơn hàng`,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  const [statistics, setStatistics] = useState({
    totalOrders: 0,
    totalItems: 0,
    totalWeight: 0,
    averageItemsPerOrder: 0
  });

  const [filters, setFilters] = useState({
    keyword: '',
    dateRange: null
  });

  const [detailModal, setDetailModal] = useState({
    visible: false,
    selectedOrder: null
  });

  // Debug mode state
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');

  // Mock data for testing when API fails
  const getMockData = () => ({
    success: true,
    data: {
      packing_orders: [
        {
          invoice: {
            id: 123,
            code: 'HD001',
            created_at: new Date().toISOString(),
            customer_name: 'Nguyễn Văn A',
            note: 'Giao hàng nhanh - Test data',
            status_id: 3,
            sub_total: 500000,
            discount_amount: 50000,
            shipping_fee: 30000,
            total_amount: 480000,
            payment_method: 'COD',
            shipping_service: 'Giao hàng nhanh',
            points_used: 0
          },
          shipping_info: {
            full_address: '123 Nguyễn Huệ, Phường 1, Quận 1, TP.HCM',
            receiver_name: 'Nguyễn Văn A',
            receiver_phone: '0901234567'
          },
          created_by: {
            name: 'Admin User',
            phone: '0987654321'
          },
          items: [
            {
              variant_id: 456,
              quantity: 2,
              price: 250000,
              line_total: 500000,
              product: {
                id: 789,
                name: 'Áo thun nam - Test Product',
                image: 'https://via.placeholder.com/100x100?text=Product'
              },
              variant: {
                sku: 'ATN-XL-RED',
                current_stock: 50,
                weight: 0.2,
                total_weight: 0.4,
                attributes: 'Size: XL, Color: Red'
              }
            }
          ],
          total_items: 1,
          total_weight: 0.4
        },
        {
          invoice: {
            id: 124,
            code: 'HD002',
            created_at: new Date(Date.now() - 86400000).toISOString(),
            customer_name: 'Trần Thị B',
            note: 'Gói cẩn thận - Test data',
            status_id: 3,
            sub_total: 750000,
            discount_amount: 0,
            shipping_fee: 25000,
            total_amount: 775000,
            payment_method: 'Chuyển khoản',
            shipping_service: 'Tiêu chuẩn',
            points_used: 50
          },
          shipping_info: {
            full_address: '456 Lê Lợi, Phường 3, Quận 3, TP.HCM',
            receiver_name: 'Trần Thị B',
            receiver_phone: '0912345678'
          },
          created_by: {
            name: 'Staff User',
            phone: '0976543210'
          },
          items: [
            {
              variant_id: 457,
              quantity: 1,
              price: 350000,
              line_total: 350000,
              product: {
                id: 790,
                name: 'Váy công sở - Test Product',
                image: 'https://via.placeholder.com/100x100?text=Dress'
              },
              variant: {
                sku: 'VCS-M-BLUE',
                current_stock: 25,
                weight: 0.3,
                total_weight: 0.3,
                attributes: 'Size: M, Color: Blue'
              }
            },
            {
              variant_id: 458,
              quantity: 2,
              price: 200000,
              line_total: 400000,
              product: {
                id: 791,
                name: 'Phụ kiện túi xách - Test Product',
                image: 'https://via.placeholder.com/100x100?text=Bag'
              },
              variant: {
                sku: 'TX-SMALL-BLACK',
                current_stock: 15,
                weight: 0.5,
                total_weight: 1.0,
                attributes: 'Size: Small, Color: Black'
              }
            }
          ],
          total_items: 2,
          total_weight: 1.3
        }
      ],
      pagination: {
        offset: 0,
        limit: 20,
        total_orders: 156,
        current_page_orders: 2,
        has_more: true
      },
      summary: {
        total_orders_returned: 2,
        total_items_count: 3,
        total_weight_all_orders: 1.7
      }
    }
  });

  useEffect(() => {
    loadPackingOrders();
  }, [pagination.current, pagination.pageSize]);

  const loadPackingOrders = async (resetPage = false) => {
    try {
      setLoading(true);
      setDebugInfo('');
      
      const offset = resetPage ? 0 : (pagination.current - 1) * pagination.pageSize;
      
      const params = {
        limit: pagination.pageSize,
        offset: offset
      };

      // Add filters if they exist
      if (filters.keyword && filters.keyword.trim()) {
        params.keyword = filters.keyword.trim();
      }

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.dateFrom = filters.dateRange[0].format('YYYY-MM-DD');
        params.dateTo = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const token = localStorage.getItem('accessToken');
      setDebugInfo(`🔄 Đang gọi API get-packing-list với params: ${JSON.stringify(params)} | Token: ${token ? token.substring(0, 20) + '...' : 'NULL'}`);
      
      const response = await packingService.getPackingList(params);

      if (response.success) {
        const { packing_orders, pagination: paginationData, summary } = response.data;
        
        setPackingOrders(packing_orders);
        
        // Update pagination
        setPagination(prev => ({
          ...prev,
          current: resetPage ? 1 : prev.current,
          total: paginationData.total_orders || 0
        }));

        // Calculate statistics - Use total from pagination for overall count
        const totalOrders = paginationData.total_orders || summary.total_orders_returned || 0;
        const totalItems = summary.total_items_count || 0;
        const totalWeight = summary.total_weight_all_orders || 0;
        
        setStatistics({
          totalOrders: totalOrders,
          totalItems: totalItems,
          totalWeight: totalWeight,
          averageItemsPerOrder: summary.total_orders_returned > 0 
            ? Math.round(totalItems / summary.total_orders_returned * 10) / 10 
            : 0
        });

        setDebugInfo(`✅ API thành công: Nhận ${packing_orders.length} đơn hàng`);
        
        message.success(`Tải thành công ${packing_orders.length} đơn hàng cần đóng gói`);
      } else {
        throw new Error(response.message || 'Không thể tải danh sách đóng gói');
      }
    } catch (error) {
      console.error('Error loading packing orders:', error);
      
      let errorMessage = '';
      let debugDetail = '';
      
      if (error.response?.status === 400) {
        errorMessage = 'Tham số không hợp lệ. Kiểm tra token hoặc dữ liệu gửi đi.';
        debugDetail = `Bad Request: ${error.response?.data?.message || 'Invalid parameters'}`;
      } else if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
        debugDetail = 'Unauthorized: Token expired or invalid';
      } else if (error.response?.status === 403) {
        errorMessage = 'Không có quyền truy cập. Chỉ admin, manager và nhân viên kho mới được xem.';
        debugDetail = 'Forbidden: Insufficient permissions';
      } else if (error.response?.status === 404) {
        errorMessage = 'API đóng gói chưa được triển khai. Vui lòng liên hệ admin.';
        debugDetail = 'Not Found: API endpoint not available';
      } else if (error.response?.status === 500) {
        errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
        debugDetail = 'Server Error: Internal server error';
      } else {
        errorMessage = 'Không thể kết nối tới server. Kiểm tra kết nối mạng.';
        debugDetail = `Network Error: ${error.message}`;
      }

      if (debugMode) {
        // Use mock data in debug mode
        const mockResponse = getMockData();
        const { packing_orders, pagination: paginationData, summary } = mockResponse.data;
        
        setPackingOrders(packing_orders);
        setPagination(prev => ({
          ...prev,
          current: resetPage ? 1 : prev.current,
          total: paginationData.total_orders || 0
        }));
        
        setStatistics({
          totalOrders: 156,
          totalItems: 62,
          totalWeight: 47.5,
          averageItemsPerOrder: 2.5
        });

        setDebugInfo(`🐛 DEBUG MODE: Sử dụng dữ liệu test do lỗi API - ${debugDetail}`);
        message.warning('Đang sử dụng dữ liệu test (Debug Mode)');
      } else {
        setDebugInfo(`❌ Lỗi API: ${debugDetail} | Status: ${error.response?.status}`);
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }));
  };

  const handleViewDetail = (order) => {
    setDetailModal({
      visible: true,
      selectedOrder: order
    });
  };

  const handleCloseDetailModal = () => {
    setDetailModal({
      visible: false,
      selectedOrder: null
    });
  };

  // Handle bàn giao cho đơn vị vận chuyển (chuyển status từ 3 -> 4)
  const handleHandoverToShipping = async (order) => {
    try {
      Modal.confirm({
        title: 'Xác nhận bàn giao vận chuyển',
        content: (
          <div>
            <p>Bạn có muốn bàn giao đơn hàng <strong>{order.invoice.code}</strong> cho đơn vị vận chuyển không?</p>
            <p>Trạng thái sẽ chuyển từ <Tag color="orange">Cần đóng gói</Tag> sang <Tag color="blue">Đang vận chuyển</Tag></p>
          </div>
        ),
        okText: 'Xác nhận bàn giao',
        cancelText: 'Hủy',
        onOk: async () => {
          try {
            setLoading(true);
            setDebugInfo(`🚚 Đang bàn giao đơn hàng ${order.invoice.code} cho vận chuyển...`);
            
            // Sử dụng API updateStatus giống như InvoiceExportForm
            const response = await invoiceService.export.updateStatus(order.invoice.id, {
              statusId: 4, // Chuyển sang trạng thái 4 (Đang vận chuyển)
              note: `Bàn giao cho ${order.invoice.shipping_service} lúc ${new Date().toLocaleString('vi-VN')}`
            });

            if (response.success) {
              message.success(`Đã bàn giao đơn hàng ${order.invoice.code} cho đơn vị vận chuyển!`);
              setDebugInfo(`✅ Bàn giao thành công: ${order.invoice.code} -> Status 4`);
              
              // Reload data để cập nhật danh sách
              loadPackingOrders();
            } else {
              throw new Error(response.message || 'Không thể cập nhật trạng thái');
            }
          } catch (error) {
            console.error('Error updating status:', error);
            setDebugInfo(`❌ Lỗi bàn giao: ${error.message}`);
            message.error('Lỗi khi bàn giao: ' + (error.response?.data?.message || error.message));
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      message.error('Lỗi hiển thị dialog xác nhận');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatWeight = (weight) => {
    if (!weight || weight === 0) {
      return 'Chưa cập nhật';
    }
    return `${weight} kg`;
  };

  const handleRefresh = () => {
    loadPackingOrders(true);
  };

  const handleSearch = () => {
    loadPackingOrders(true);
  };

  const handleClearFilters = () => {
    setFilters({
      keyword: '',
      dateRange: null
    });
    setTimeout(() => loadPackingOrders(true), 100);
  };

  const columns = [
    {
      title: 'Mã đơn hàng',
      dataIndex: ['invoice', 'code'],
      key: 'code',
      width: 140,
      render: (code) => (
        <Text strong style={{ color: '#1890ff' }}>{code}</Text>
      ),
      sorter: true
    },
    {
      title: 'Khách hàng',
      dataIndex: ['invoice', 'customer_name'],
      key: 'customer_name',
      width: 150,
      render: (name) => (
        <Space>
          <UserOutlined style={{ color: '#52c41a' }} />
          <Text>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Người nhận',
      dataIndex: ['shipping_info'],
      key: 'receiver',
      width: 180,
      render: (shippingInfo) => (
        <div>
          <Text strong>{shippingInfo.receiver_name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <PhoneOutlined style={{ marginRight: 4 }} />
            {shippingInfo.receiver_phone}
          </Text>
        </div>
      )
    },
    {
      title: 'Địa chỉ giao hàng',
      dataIndex: ['shipping_info', 'full_address'],
      key: 'address',
      width: 250,
      render: (address) => (
        <Tooltip title={address}>
          <Text ellipsis style={{ maxWidth: 230, display: 'block' }}>
            <HomeOutlined style={{ marginRight: 4, color: '#fa8c16' }} />
            {address}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Số sản phẩm',
      dataIndex: 'total_items',
      key: 'total_items',
      width: 120,
      render: (count) => (
        <Badge count={count} style={{ backgroundColor: '#52c41a' }} />
      ),
      sorter: true
    },
    {
      title: 'Tổng trọng lượng',
      dataIndex: 'total_weight',
      key: 'total_weight',
      width: 120,
      render: (weight) => (
        <Space>
          <DatabaseOutlined style={{ color: '#722ed1' }} />
          <Text>{formatWeight(weight)}</Text>
        </Space>
      ),
      sorter: true
    },
    {
      title: 'Tổng tiền',
      dataIndex: ['invoice', 'total_amount'],
      key: 'total_amount',
      width: 130,
      render: (amount) => (
        <Text strong style={{ color: '#f5222d' }}>
          {formatCurrency(amount)}
        </Text>
      ),
      sorter: true
    },
    {
      title: 'Ngày tạo',
      dataIndex: ['invoice', 'created_at'],
      key: 'created_at',
      width: 120,
      render: (date) => (
        <Space>
          <CalendarOutlined style={{ color: '#1890ff' }} />
          <Text style={{ fontSize: '12px' }}>
            {new Date(date).toLocaleDateString('vi-VN')}
          </Text>
        </Space>
      ),
      sorter: true
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết đóng gói">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              Chi tiết
            </Button>
          </Tooltip>
          <Tooltip title="Bàn giao cho đơn vị vận chuyển">
            <Button
              type="primary"
              size="small"
              icon={<TruckOutlined />}
              onClick={() => handleHandoverToShipping(record)}
              disabled={record.invoice.status_id !== 3} // Chỉ cho phép với status 3
            >
              Bàn giao
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Row align="middle" justify="space-between">
              <Col>
                <Title level={3} style={{ margin: 0 }}>
                  <InboxOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                  Đóng gói hàng - Danh sách cần xử lý
                </Title>
                <Text type="secondary">
                  Quản lý danh sách đơn hàng cần đóng gói và vận chuyển
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={loading}
                  >
                    Làm mới
                  </Button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BugOutlined style={{ color: debugMode ? '#ff4d4f' : '#d9d9d9' }} />
                    <Switch
                      checked={debugMode}
                      onChange={setDebugMode}
                      size="small"
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Debug Mode
                    </Text>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Debug Information */}
      {debugMode && debugInfo && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Alert
              message="Debug Information"
              description={debugInfo}
              type="info"
              showIcon
              closable
              onClose={() => setDebugInfo('')}
            />
          </Col>
        </Row>
      )}

      {/* API Status Notice */}
      <Alert
        message="Thông báo về chức năng đóng gói"
        description={
          <div>
            <p>📦 <strong>Danh sách đơn hàng cần đóng gói (Status = 3 - Đã gửi cho đơn vị vận chuyển)</strong></p>
            <p>• <strong>Quyền truy cập:</strong> Admin, Manager và Nhân viên kho</p>
            <p>• <strong>Thông tin hiển thị:</strong> Chi tiết sản phẩm, địa chỉ giao hàng, trọng lượng</p>
            <p>• <strong>Tính năng:</strong> Tìm kiếm, lọc theo ngày, phân trang, xem chi tiết</p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
        closable
      />

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng đơn hàng"
              value={statistics.totalOrders}
              prefix={<ShoppingCartOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng sản phẩm"
              value={statistics.totalItems}
              prefix={<InboxOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng trọng lượng"
              value={statistics.totalWeight}
              suffix="kg"
              precision={1}
              prefix={<DatabaseOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="TB sản phẩm/đơn"
              value={statistics.averageItemsPerOrder}
              precision={1}
              prefix={<ShoppingCartOutlined style={{ color: '#fa8c16' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Search
            placeholder="Tìm theo mã đơn, khách hàng, ghi chú..."
            value={filters.keyword}
            onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            onSearch={handleSearch}
            enterButton={<SearchOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
            placeholder={['Từ ngày', 'Đến ngày']}
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Space>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleSearch}
            >
              Áp dụng bộ lọc
            </Button>
            <Button onClick={handleClearFilters}>
              Xóa bộ lọc
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Main Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={packingOrders}
          loading={loading}
          rowKey={(record) => record.invoice.id}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          size="middle"
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <InboxOutlined style={{ color: '#722ed1' }} />
            Chi tiết đơn hàng đóng gói
          </Space>
        }
        visible={detailModal.visible}
        onCancel={handleCloseDetailModal}
        footer={[
          <Button key="close" onClick={handleCloseDetailModal}>
            Đóng
          </Button>
        ]}
        width={900}
        destroyOnClose
      >
        {detailModal.selectedOrder && (
          <div>
            {/* Order Info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>Thông tin đơn hàng</Title>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Mã đơn hàng">
                  <Text strong>{detailModal.selectedOrder.invoice.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Khách hàng">
                  {detailModal.selectedOrder.invoice.customer_name}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày tạo">
                  {new Date(detailModal.selectedOrder.invoice.created_at).toLocaleString('vi-VN')}
                </Descriptions.Item>
                <Descriptions.Item label="Tổng tiền">
                  {formatCurrency(detailModal.selectedOrder.invoice.total_amount)}
                </Descriptions.Item>
                <Descriptions.Item label="Phương thức thanh toán">
                  {detailModal.selectedOrder.invoice.payment_method}
                </Descriptions.Item>
                <Descriptions.Item label="Dịch vụ vận chuyển">
                  {detailModal.selectedOrder.invoice.shipping_service}
                </Descriptions.Item>
                <Descriptions.Item label="Ghi chú" span={2}>
                  {detailModal.selectedOrder.invoice.note || 'Không có ghi chú'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Shipping Info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>Thông tin giao hàng</Title>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Người nhận">
                  {detailModal.selectedOrder.shipping_info.receiver_name}
                </Descriptions.Item>
                <Descriptions.Item label="Số điện thoại">
                  {detailModal.selectedOrder.shipping_info.receiver_phone}
                </Descriptions.Item>
                <Descriptions.Item label="Địa chỉ" span={2}>
                  {detailModal.selectedOrder.shipping_info.full_address}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Items List */}
            <Card size="small">
              <Title level={5}>Danh sách sản phẩm cần đóng gói</Title>
              <Table
                dataSource={detailModal.selectedOrder.items}
                pagination={false}
                size="small"
                rowKey="variant_id"
                columns={[
                  {
                    title: 'Hình ảnh',
                    dataIndex: ['product', 'image'],
                    key: 'image',
                    width: 80,
                    render: (image, record) => (
                      <Image
                        width={50}
                        height={50}
                        src={image}
                        alt={record.product.name}
                        style={{ objectFit: 'cover' }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Yk1xUE8A8B6AC2C4BdALYAIwAkBI7iJAg"
                      />
                    )
                  },
                  {
                    title: 'Sản phẩm',
                    dataIndex: 'product',
                    key: 'product',
                    render: (product, record) => (
                      <div>
                        <Text strong>{product.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          SKU: {record.variant.sku}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {record.variant.attributes}
                        </Text>
                      </div>
                    )
                  },
                  {
                    title: 'Số lượng',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    width: 80,
                    render: (qty) => (
                      <Badge count={qty} style={{ backgroundColor: '#52c41a' }} />
                    )
                  },
                  {
                    title: 'Trọng lượng',
                    dataIndex: ['variant', 'total_weight'],
                    key: 'weight',
                    width: 100,
                    render: (weight) => (
                      <Text>{formatWeight(weight)}</Text>
                    )
                  },
                  {
                    title: 'Đơn giá',
                    dataIndex: 'price',
                    key: 'price',
                    width: 120,
                    render: (price) => (
                      <Text>{formatCurrency(price)}</Text>
                    )
                  },
                  {
                    title: 'Thành tiền',
                    dataIndex: 'line_total',
                    key: 'line_total',
                    width: 120,
                    render: (total) => (
                      <Text strong style={{ color: '#f5222d' }}>
                        {formatCurrency(total)}
                      </Text>
                    )
                  }
                ]}
                summary={(pageData) => {
                  let totalWeight = 0;
                  let totalAmount = 0;
                  let totalQuantity = 0;

                  pageData.forEach(({ quantity, line_total, variant }) => {
                    totalQuantity += quantity;
                    totalWeight += variant.total_weight;
                    totalAmount += line_total;
                  });

                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={2}>
                        <Text strong>Tổng cộng</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Badge count={totalQuantity} style={{ backgroundColor: '#722ed1' }} />
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong>{formatWeight(totalWeight)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell colSpan={1}></Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong style={{ color: '#f5222d' }}>
                          {formatCurrency(totalAmount)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>

            <Divider />

            {/* Created By Info */}
            {detailModal.selectedOrder.created_by && (
              <Card size="small">
                <Title level={5}>Thông tin người tạo đơn</Title>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Tên">
                    {detailModal.selectedOrder.created_by.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Số điện thoại">
                    {detailModal.selectedOrder.created_by.phone}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PackingList;